import i18nFactory from '@yandex-int/i18n';

import { EditGroupErrorsRecord, GroupId } from '../../../typings/groups';
import { EditInternalGroupData } from '../../../typings/groupsEditState';
import {
    AdditionalTargetingField,
    InternalGroupField,
    RestInternalGroupField,
    TargetingGroupType
} from '../../../typings/internalGroups';
import { ErrorOrWarning } from '../../ErrorHelpers/ErrorHelpers';
import { InternalGroupDefectCodes } from '../InternalGroupValidator/InternalGroupValidator';

import * as keyset from './InternalGroupExtractErrorsHelpers.i18n';

const i18n = i18nFactory(keyset);

const mapErrorCodeToText = (field: InternalGroupField, errorCode: string): string => {
    switch (errorCode) {
        case InternalGroupDefectCodes.CANNOT_BE_EMPTY:
            return i18n('canNotBeEmpty');
        case InternalGroupDefectCodes.FINISH_TIME_SHOULD_BE_GREATER_THAN_START_TIME:
            return i18n('finishTimeShouldBeGreaterThanStartTime');
        case InternalGroupDefectCodes.VERSIONED_TARGETING_WITH_MULTIPLE_VERSIONS:
            return i18n('multipleVersions');
        case InternalGroupDefectCodes.INCORRECT_VERSION:
            return i18n('incorrectVersion');
        case InternalGroupDefectCodes.MUST_BE_GREATER_THAN_MIN:
            return i18n('numericNegativeValue');
        case InternalGroupDefectCodes.EMPTY_REGIONS:
            return i18n('emptyRegions');
        default:
            break;
    }

    switch (field) {
        case AdditionalTargetingField.OS_NAMES:
        case AdditionalTargetingField.OS_FAMILIES:
        case AdditionalTargetingField.BROWSER_NAMES:
        case AdditionalTargetingField.BROWSER_ENGINES:
        case AdditionalTargetingField.DESKTOP_INSTALLED_APPS:
        case AdditionalTargetingField.DEVICE_VENDORS:
        case AdditionalTargetingField.INTERFACE_LANGS:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('suggestInvalidValue');
            }

            return i18n('unknownError');

        case AdditionalTargetingField.CLIDS:
        case AdditionalTargetingField.CLID_TYPES:
        case AdditionalTargetingField.TEST_IDS:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('numericInvalidValue');
            }

            return i18n('unknownError');

        case AdditionalTargetingField.MOBILE_INSTALLED_APPS:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('invalidUrl');
            }

            return i18n('unknownError');
        case AdditionalTargetingField.QUERY_OPTIONS:
        case AdditionalTargetingField.QUERY_REFERERS:
        case AdditionalTargetingField.YS_COOKIES:
        case AdditionalTargetingField.USER_AGENTS:
        case AdditionalTargetingField.DEVICE_NAMES:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('invalidValue');
            }

            return i18n('unknownError');

        case AdditionalTargetingField.YANDEX_UIDS:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('invalidYandexUid');
            }

            return i18n('unknownError');

        case AdditionalTargetingField.SHOW_DATES:
            if (errorCode === InternalGroupDefectCodes.INVALID_VALUE) {
                return i18n('invalidDate');
            }

            return i18n('unknownError');

        case RestInternalGroupField.LEVEL:
            if (errorCode === InternalGroupDefectCodes.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX) {
                return i18n('valueShouldBeLessThan', { maxValue: '1000' });
            }

            if (errorCode === InternalGroupDefectCodes.MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN) {
                return i18n('valueShouldBeMoreThan', { minValue: '0' });
            }

            return i18n('unknownError');

        default:
            return i18n('unknownError');
    }
};

const extractGroupIdFromErrorPath = (path: string, groupIds: GroupId[]): GroupId => {
    return groupIds[Number(path.split(/[\]\[]/)[1])];
};

export const extractFieldFromErrorPath = (path: string): InternalGroupField => {
    return path.split('.')[1] as InternalGroupField;
};

export const mapInternalGroupValidationErrors = (
    errors: ErrorOrWarning[],
    groupIds: GroupId[]
): EditGroupErrorsRecord => {
    return errors.reduce((acc: EditGroupErrorsRecord, error: ErrorOrWarning): EditGroupErrorsRecord => {
        const groupId = extractGroupIdFromErrorPath(error.path, groupIds);
        const fieldName = extractFieldFromErrorPath(error.path);

        if (acc[groupId] && acc[groupId][fieldName]) {
            return {
                ...acc,
                [groupId]: {
                    ...acc[groupId],
                    [fieldName]: {
                        errors: [
                            ...acc[groupId][fieldName].errors,
                            {
                                text: mapErrorCodeToText(fieldName, error.code)
                            }
                        ]
                    }
                }
            };
        }

        if (acc[groupId] && !acc[groupId][fieldName]) {
            return {
                ...acc,
                [groupId]: {
                    ...acc[groupId],
                    [fieldName]: {
                        errors: [
                            {
                                text: mapErrorCodeToText(fieldName, error.code)
                            }
                        ]
                    }
                }
            };
        }

        return {
            ...acc,
            [groupId]: {
                [fieldName]: {
                    errors: [
                        {
                            text: mapErrorCodeToText(fieldName, error.code)
                        }
                    ]
                }
            }
        };
    }, {});
};

const getFieldsForTargetingGroup = (type: TargetingGroupType): AdditionalTargetingField[] => {
    switch (type) {
        case TargetingGroupType.CORE:
            return [
                AdditionalTargetingField.INTERNAL_NETWORK,
                AdditionalTargetingField.INTERFACE_LANGS,
                AdditionalTargetingField.QUERY_REFERERS
            ];

        case TargetingGroupType.DISTRIBUTION:
            return [
                AdditionalTargetingField.USER_AGENTS,
                AdditionalTargetingField.DESKTOP_INSTALLED_APPS,
                AdditionalTargetingField.MOBILE_INSTALLED_APPS,
                AdditionalTargetingField.YS_COOKIES,
                AdditionalTargetingField.CLIDS,
                AdditionalTargetingField.CLID_TYPES
            ];

        case TargetingGroupType.TECHNICAL:
            return [AdditionalTargetingField.YANDEX_UIDS, AdditionalTargetingField.TEST_IDS];

        case TargetingGroupType.DEVICE:
            return [
                AdditionalTargetingField.DEVICE_VENDORS,
                AdditionalTargetingField.DEVICE_NAMES,
                AdditionalTargetingField.DEVICE_OPTIONS
            ];

        case TargetingGroupType.OS:
            return [AdditionalTargetingField.OS_FAMILIES, AdditionalTargetingField.OS_NAMES];

        case TargetingGroupType.BROWSER:
            return [AdditionalTargetingField.BROWSER_ENGINES, AdditionalTargetingField.BROWSER_NAMES];

        case TargetingGroupType.OTHER:
            return [AdditionalTargetingField.QUERY_OPTIONS, AdditionalTargetingField.IS_YANDEX_PLUS];

        default:
            return [];
    }
};

export const getErrorsCountForTargetingsGroup = (group: EditInternalGroupData, type: TargetingGroupType): number => {
    const currentFields = getFieldsForTargetingGroup(type);
    const { errors } = group;

    return currentFields.reduce((acc: number, field: AdditionalTargetingField): number => {
        const fieldErrors = errors[field];

        if (!fieldErrors || !fieldErrors.errors) {
            return acc;
        }

        return acc + fieldErrors.errors.length;
    }, 0);
};
